import {
    getAccountKeyForMailboxInfo,
    isDefaultAccountKey,
    type MailboxInfo,
} from 'owa-client-types';
import {
    isSameMailboxInfo,
    tryGetGlobalSettingsAccountMailboxInfo,
} from 'owa-account-source-list-store';
import type { CalendarEntry, LinkedCalendarEntry } from 'owa-graph-schema';

export function getCalendarOwnerIdentity(calendarEntry: CalendarEntry): string | undefined {
    return (calendarEntry as LinkedCalendarEntry).OwnerEmailAddress?.trim().toLowerCase();
}

export function areCalendarEntriesInSameAccount(
    firstCalendarEntry: CalendarEntry,
    secondCalendarEntry: CalendarEntry
): boolean {
    return areMailboxInfosInSameAccount(
        firstCalendarEntry.calendarId.mailboxInfo,
        secondCalendarEntry.calendarId.mailboxInfo
    );
}

export function areMailboxInfosInSameAccount(
    firstMailboxInfo: MailboxInfo,
    secondMailboxInfo: MailboxInfo
): boolean {
    const firstAccountKey = getAccountKeyForMailboxInfo(firstMailboxInfo);
    const secondAccountKey = getAccountKeyForMailboxInfo(secondMailboxInfo);

    if (firstAccountKey !== secondAccountKey) {
        return false;
    }

    // The default account key is also the fallback for mailboxes whose account can't be resolved,
    // so two default keys only prove the same account when the global settings account itself
    // resolves to the default key.
    if (isDefaultAccountKey(firstAccountKey)) {
        const globalSettingsMailboxInfo = tryGetGlobalSettingsAccountMailboxInfo(
            false /* logGreyError */
        );
        return (
            !!globalSettingsMailboxInfo &&
            isSameMailboxInfo(firstMailboxInfo, globalSettingsMailboxInfo) &&
            isSameMailboxInfo(secondMailboxInfo, globalSettingsMailboxInfo)
        );
    }

    return true;
}

export function areCalendarEntriesOrganizationDuplicates(
    firstCalendarEntry: CalendarEntry,
    secondCalendarEntry: CalendarEntry,
    firstIsOrganizationCalendar: boolean,
    secondIsOrganizationCalendar: boolean
): boolean {
    const firstOwnerIdentity = getCalendarOwnerIdentity(firstCalendarEntry);
    const secondOwnerIdentity = getCalendarOwnerIdentity(secondCalendarEntry);

    return (
        firstCalendarEntry.calendarId.id !== secondCalendarEntry.calendarId.id &&
        (firstIsOrganizationCalendar || secondIsOrganizationCalendar) &&
        areCalendarEntriesInSameAccount(firstCalendarEntry, secondCalendarEntry) &&
        !!firstOwnerIdentity &&
        firstOwnerIdentity === secondOwnerIdentity
    );
}
